import { computed, ref } from 'vue';
import { defineStore } from 'pinia';
import type { CallEndReason, PublicUser, ServerFrame } from '@talkvibe/shared';

import { iceConfig } from '../services/calls.ts';
import { RealtimeConnection } from '../services/websocket.ts';

/**
 * One call at a time, from either end.
 *
 * **Where the audio goes.** Nowhere near this product. The two browsers connect
 * directly over WebRTC; the server relays an offer, an answer and a handful of
 * candidates, and then it is not involved. That is why there is no bandwidth
 * cost per minute and no media server to run — and also why a call can fail for
 * reasons no server log will show, which is what `relayAvailable` below is for.
 *
 * **Why this store owns its own socket.** The chat store opens a conversation
 * socket while a chat is on screen and closes it on the way out. A call must
 * outlive that: it starts from the inbox, it survives walking to the profile
 * page, and hanging up is the only thing that should end it. So the signalling
 * runs over a second connection to the same room, opened when a call starts and
 * closed when it ends. The server keys by socket, so two from one person is
 * ordinary.
 *
 * **Audio only, deliberately.** Video is the same signalling with a camera
 * track, and it is not the hard part; what it adds is a UI with two video
 * surfaces, a layout on a phone, and bandwidth that makes a relay far more
 * expensive. Calling works first.
 *
 * **Two known limits, both visible rather than hidden.** A device with no
 * socket open cannot be rung — a phone with the app closed is unreachable
 * until this product has push. And if two people press Call in the same
 * moment, each has a local offer outstanding, so each refuses the other's with
 * `busy` and neither connects: both are told why, and resolving it properly
 * needs an offer/rollback tie-break that is not built.
 */

/**
 * The call's state, which is also what the overlay renders.
 *
 * `ringing-in` and `ringing-out` are separate because almost nothing about them
 * is shared: one offers Accept and Decline, the other offers Cancel, and the
 * one thing they have in common — a name and a face — is the easy part.
 */
export type CallPhase =
  | 'idle'
  | 'ringing-out'
  | 'ringing-in'
  | 'connecting'
  | 'active'
  | 'ended'
  | 'failed';

/** Who is on the other end, as much as is known when the ring arrives. */
export interface CallPeer {
  id: string;
  username: string;
  displayName: string;
  avatarUrl: string | null;
}

/**
 * How long a ring lasts before the caller gives up.
 *
 * Thirty seconds, which is about what a phone does. Shorter and somebody in
 * another room has no chance; longer and a call to a laptop nobody is at goes
 * on ringing into an empty flat.
 */
const RING_TIMEOUT_MS = 30_000;

/**
 * How often the caller repeats the offer while ringing.
 *
 * **This is what lets the callee answer at all.** The offer goes out over the
 * conversation socket, which relays it to whoever is connected *at that moment*
 * — and the person being called almost never is: they get the ring on their
 * user channel, and only then open the conversation socket. The first offer is
 * long gone by the time they do.
 *
 * Repeating it is how SIP has always handled the same problem. Every repeat
 * carries the same SDP, so a callee who already has it does nothing with the
 * second copy; one who has just arrived gets a complete offer within two
 * seconds without any extra frame type, and the same mechanism covers a
 * reconnect mid-ring and a frame lost in a tunnel.
 */
const REOFFER_INTERVAL_MS = 2_000;

/**
 * How long a connection may stay in `connecting` before it is called failed.
 *
 * ICE itself gives up eventually, but slowly and silently, and `disconnected`
 * can recover on its own — so waiting for the browser means a caller watching a
 * spinner with nothing to click for upwards of half a minute. Fifteen seconds
 * after an answer is long enough for a relayed path to come up and short enough
 * to be a message instead of a guess.
 */
const CONNECT_TIMEOUT_MS = 15_000;

export const useCallStore = defineStore('call', () => {
  const phase = ref<CallPhase>('idle');
  const peer = ref<CallPeer | null>(null);
  const conversationId = ref<string | null>(null);
  const callId = ref<string | null>(null);
  const muted = ref(false);
  /** Seconds since the call connected, for the timer in the overlay. */
  const seconds = ref(0);
  /**
   * Why it ended, kept after `phase` becomes `ended` so the overlay can say
   * "Declined" rather than just closing — a call that vanishes leaves somebody
   * wondering whether it was them.
   */
  const endReason = ref<CallEndReason | null>(null);
  /** Set when a relay would have been needed and there is none. */
  const relayMissing = ref(false);
  /** The browser's own refusal, which is usually about the microphone. */
  const errorKey = ref<string | null>(null);

  let peerConnection: RTCPeerConnection | null = null;
  let connection: RealtimeConnection | null = null;
  let localStream: MediaStream | null = null;
  let remoteAudio: HTMLAudioElement | null = null;
  let ringTimer: number | null = null;
  let reofferTimer: number | null = null;
  let connectTimer: number | null = null;
  let tickTimer: number | null = null;
  /** The caller's own offer, kept so every repeat is byte-identical. */
  let localOffer: string | null = null;
  /**
   * The caller's own candidates, kept so the repeat can carry them too.
   *
   * **Without this a call rings, is answered, and has no sound.** ICE starts
   * gathering the moment `setLocalDescription` runs, and those candidates are
   * sent over this room's socket — which the callee has not opened yet. They
   * are rung on the user channel and join the room only on answering, and the
   * server relays to whoever is present rather than buffering, so every one of
   * them is broadcast into an empty room and lost.
   *
   * The offer survived that because it is repeated; the candidates were not,
   * so the callee ended up with a media description and not one address to try.
   * They are resent with each repeat, and only until the answer arrives — past
   * that both sides are in the room and ordinary trickling delivers.
   */
  let localCandidates: Array<{
    candidate: string;
    sdpMid?: string;
    sdpMLineIndex?: number;
  }> = [];
  /**
   * Candidates that arrived before there was a remote description to attach
   * them to.
   *
   * `addIceCandidate` throws in that window, and the window is real: an offer
   * and its candidates are sent back to back, and they do not arrive in that
   * order — the relay handles each frame in its own task, so a candidate
   * routinely overtakes the offer it belongs to. Dropping them costs connection
   * paths, which presents as a call that connects on a good network and not on
   * a bad one.
   */
  let pendingCandidates: RTCIceCandidateInit[] = [];

  /**
   * Whether a call is actually live.
   *
   * It guards starting one, answering one, and the Call button in the header,
   * so the list of phases it excludes is the whole definition — and it first
   * read `!== 'idle' && !== 'ended'`, which counted `failed` as live. The panel
   * deliberately stays on screen after a failure so the reason can be read,
   * so that one word meant a dead panel refused every new call in both
   * directions until somebody found the Close button.
   */
  const busy = computed(
    () => phase.value !== 'idle' && phase.value !== 'ended' && phase.value !== 'failed',
  );
  /** Whether the panel is on screen, which outlasts the call by design. */
  const inCall = computed(() => phase.value !== 'idle');

  /* ---------------------------------------------------------------------- */
  /* Placing and answering                                                  */
  /* ---------------------------------------------------------------------- */

  /**
   * Starts a call to the other person in a conversation.
   *
   * The microphone is asked for *before* anything is sent. A caller who then
   * refuses the prompt would otherwise have set the other phone ringing for a
   * call with no audio to offer.
   */
  async function place(conversation: string, other: CallPeer): Promise<void> {
    if (busy.value) return;
    reset();
    conversationId.value = conversation;
    peer.value = other;
    callId.value = mintCallId();
    phase.value = 'ringing-out';

    try {
      await openMicrophone();
      const pc = await createPeerConnection();
      const offer = await pc.createOffer({ offerToReceiveAudio: true });
      await pc.setLocalDescription(offer);
      localOffer = offer.sdp ?? '';
      openSignalling(conversation);
      sendOffer();
      // Repeated until answered, because the callee is not connected yet.
      reofferTimer = window.setInterval(sendOffer, REOFFER_INTERVAL_MS);
      ringTimer = window.setTimeout(() => finish('unanswered', true), RING_TIMEOUT_MS);
    } catch (error) {
      failWith(error);
    }
  }

  /**
   * A ring arriving on the user channel.
   *
   * No SDP comes with it — the offer travels on the conversation socket, which
   * is not open yet. All this does is put the overlay on screen; the offer is
   * collected by `accept`, from the repeats the caller is sending.
   */
  function incoming(payload: {
    callId: string;
    conversationId: string;
    from: CallPeer;
  }): void {
    // Already busy: tell the caller so, rather than letting their call ring out
    // on a device whose owner is mid-conversation.
    if (busy.value) {
      declineFromElsewhere(payload.conversationId, payload.callId, 'busy');
      return;
    }
    reset();
    callId.value = payload.callId;
    conversationId.value = payload.conversationId;
    peer.value = payload.from;
    phase.value = 'ringing-in';
    // The callee gives up too. A caller who closed their laptop sends no
    // `call.end`, and without this the overlay would ring until it was clicked.
    ringTimer = window.setTimeout(() => finish('unanswered', false), RING_TIMEOUT_MS);
  }

  /** Accepts the incoming call: microphone, then wait for the next offer. */
  async function accept(): Promise<void> {
    if (phase.value !== 'ringing-in' || !conversationId.value) return;
    phase.value = 'connecting';
    clearTimer('ring');
    try {
      await openMicrophone();
      await createPeerConnection();
      // Opening the socket is what makes the next repeated offer reach here.
      openSignalling(conversationId.value);
      connectTimer = window.setTimeout(() => failWith(new Error('timeout')), CONNECT_TIMEOUT_MS);
    } catch (error) {
      // The microphone was refused, so there is nothing to answer with. The
      // caller is told, or they watch a ring that was already accepted.
      failWith(error);
      declineFromElsewhere(conversationId.value, callId.value ?? '', 'declined');
    }
  }

  function decline(): void {
    finish('declined', true);
  }

  function hangUp(): void {
    finish('hangup', true);
  }

  function toggleMute(): void {
    muted.value = !muted.value;
    for (const track of localStream?.getAudioTracks() ?? []) {
      track.enabled = !muted.value;
    }
  }

  /** Clears an `ended` or `failed` overlay off the screen. */
  function dismiss(): void {
    if (busy.value) return;
    reset();
  }

  /* ---------------------------------------------------------------------- */
  /* Signalling                                                             */
  /* ---------------------------------------------------------------------- */

  function openSignalling(conversation: string): void {
    connection?.disconnect();
    connection = new RealtimeConnection(conversation, {
      onFrame: handleFrame,
      onStateChange: () => {
        // Deliberately ignored. The reconnecting socket handles an outage on
        // its own, and a call's health is the peer connection's business, not
        // this socket's — signalling is finished within a second or two of
        // connecting, and a call survives the socket dropping afterwards.
      },
    });
    void connection.connect();
  }

  /**
   * The offer, and everything gathered so far, to whoever is in the room now.
   *
   * The candidates ride along with the repeat rather than being trickled only
   * once — see `localCandidates` for why a call is silent without that.
   */
  function sendOffer(): void {
    if (!connection || !callId.value || localOffer === null) return;
    connection.send({
      type: 'call.offer',
      conversationId: conversationId.value ?? '',
      payload: { callId: callId.value, sdp: localOffer },
    });
    for (const candidate of localCandidates) {
      connection.send({
        type: 'call.candidate',
        conversationId: conversationId.value ?? '',
        payload: { callId: callId.value, ...candidate },
      });
    }
  }

  function handleFrame(frame: ServerFrame): void {
    switch (frame.type) {
      case 'call.offer':
        void receiveOffer(frame.payload);
        return;
      case 'call.answer':
        void receiveAnswer(frame.payload);
        return;
      case 'call.candidate':
        void receiveCandidate(frame.payload);
        return;
      case 'call.end':
        if (frame.payload.callId !== callId.value) return;
        finish(frame.payload.reason, false);
        return;
      default:
        // Every other frame on this socket is the chat's — messages, typing,
        // read receipts. The chat store has its own connection and handles
        // them there; answering them here as well would double every message.
        return;
    }
  }

  async function receiveOffer(payload: { callId: string; sdp: string }): Promise<void> {
    const pc = peerConnection;
    // Only while answering, and only this call. A repeat arriving after the
    // answer is a duplicate the caller sent before hearing back, and setting it
    // would restart a negotiation that has already succeeded.
    if (!pc || payload.callId !== callId.value) return;
    if (pc.signalingState !== 'stable' || pc.remoteDescription) return;

    try {
      await pc.setRemoteDescription({ type: 'offer', sdp: payload.sdp });
      await flushCandidates();
      const answer = await pc.createAnswer();
      await pc.setLocalDescription(answer);
      connection?.send({
        type: 'call.answer',
        conversationId: conversationId.value ?? '',
        payload: { callId: payload.callId, sdp: answer.sdp ?? '' },
      });
    } catch (error) {
      failWith(error);
    }
  }

  async function receiveAnswer(payload: { callId: string; sdp: string }): Promise<void> {
    const pc = peerConnection;
    if (!pc || payload.callId !== callId.value) return;
    // An answer is only meaningful against an offer still waiting for one.
    if (pc.signalingState !== 'have-local-offer') return;

    // Answered: stop ringing and stop repeating. Both, because either one left
    // running is its own bug — a ring timer that fires mid-call hangs up a
    // working call, and a repeat after the answer restarts the negotiation.
    clearTimer('ring');
    clearTimer('reoffer');
    phase.value = 'connecting';
    connectTimer = window.setTimeout(() => failWith(new Error('timeout')), CONNECT_TIMEOUT_MS);

    try {
      await pc.setRemoteDescription({ type: 'answer', sdp: payload.sdp });
      await flushCandidates();
    } catch (error) {
      failWith(error);
    }
  }

  async function receiveCandidate(payload: {
    callId: string;
    candidate: string;
    sdpMid?: string;
    sdpMLineIndex?: number;
  }): Promise<void> {
    const pc = peerConnection;
    if (!pc || payload.callId !== callId.value) return;
    const candidate: RTCIceCandidateInit = {
      candidate: payload.candidate,
      ...(payload.sdpMid !== undefined ? { sdpMid: payload.sdpMid } : {}),
      ...(payload.sdpMLineIndex !== undefined ? { sdpMLineIndex: payload.sdpMLineIndex } : {}),
    };
    if (!pc.remoteDescription) {
      pendingCandidates.push(candidate);
      return;
    }
    try {
      await pc.addIceCandidate(candidate);
    } catch {
      // One candidate the browser will not take is one path not tried, not a
      // failed call. ICE has others.
    }
  }

  async function flushCandidates(): Promise<void> {
    const queued = pendingCandidates;
    pendingCandidates = [];
    for (const candidate of queued) {
      try {
        await peerConnection?.addIceCandidate(candidate);
      } catch {
        // As above.
      }
    }
  }

  /* ---------------------------------------------------------------------- */
  /* The peer connection                                                    */
  /* ---------------------------------------------------------------------- */

  async function openMicrophone(): Promise<void> {
    if (localStream) return;
    localStream = await navigator.mediaDevices.getUserMedia({
      // Echo cancellation is not a nicety here: both sides are usually on a
      // laptop with the speaker a few centimetres from the microphone.
      audio: { echoCancellation: true, noiseSuppression: true, autoGainControl: true },
      video: false,
    });
  }

  async function createPeerConnection(): Promise<RTCPeerConnection> {
    const config = await iceConfig();
    relayMissing.value = false;
    const pc = new RTCPeerConnection({ iceServers: config.iceServers });
    peerConnection = pc;

    for (const track of localStream?.getTracks() ?? []) {
      pc.addTrack(track, localStream!);
    }

    pc.addEventListener('icecandidate', (event) => {
      if (!event.candidate || !callId.value) return;
      const candidate = {
        candidate: event.candidate.candidate,
        ...(event.candidate.sdpMid !== null ? { sdpMid: event.candidate.sdpMid } : {}),
        ...(event.candidate.sdpMLineIndex !== null
          ? { sdpMLineIndex: event.candidate.sdpMLineIndex }
          : {}),
      };
      // Kept as well as sent. Sending alone loses every candidate gathered
      // before the callee joined the room, which is most of them.
      if (reofferTimer !== null) localCandidates.push(candidate);
      connection?.send({
        type: 'call.candidate',
        conversationId: conversationId.value ?? '',
        payload: { callId: callId.value, ...candidate },
      });
    });

    pc.addEventListener('track', (event) => {
      // Played through an element this store owns rather than one in a
      // component, so the audio does not stop when the overlay re-renders or
      // the user navigates to another page mid-call.
      remoteAudio ??= new Audio();
      remoteAudio.autoplay = true;
      remoteAudio.srcObject = event.streams[0] ?? new MediaStream([event.track]);
      void remoteAudio.play().catch(() => {
        // Autoplay refused. The user clicked Accept or Call a moment ago, so
        // this is rare; if it happens there is nothing useful to do but let the
        // call continue silently rather than tear it down.
      });
    });

    pc.addEventListener('connectionstatechange', () => {
      if (pc !== peerConnection) return;
      if (pc.connectionState === 'connected') {
        clearTimer('connect');
        if (phase.value !== 'active') startTimer();
        phase.value = 'active';
        return;
      }
      if (pc.connectionState === 'failed') {
        // The one failure worth explaining. ICE exhausting every candidate
        // without a path is what happens between two symmetric NATs, and
        // whether a relay existed is the difference between "your network" and
        // "this product has no relay configured".
        void iceConfig()
          .then((current) => {
            relayMissing.value = !current.relayAvailable;
          })
          .catch(() => undefined);
        failWith(new Error('ice'));
      }
    });

    return pc;
  }

  /* ---------------------------------------------------------------------- */
  /* Ending                                                                 */
  /* ---------------------------------------------------------------------- */

  /**
   * Ends the call, optionally telling the other side.
   *
   * `announce` is false when the other side is the one who ended it — echoing
   * their own hangup back is how two clients can keep answering each other.
   */
  function finish(reason: CallEndReason, announce: boolean): void {
    if (phase.value === 'idle') return;
    if (announce && connection && callId.value) {
      connection.send({
        type: 'call.end',
        conversationId: conversationId.value ?? '',
        payload: { callId: callId.value, reason },
      });
    }
    endReason.value = reason;
    phase.value = 'ended';
    teardown();
  }

  function failWith(error: unknown): void {
    if (phase.value === 'idle' || phase.value === 'ended') return;
    errorKey.value = microphoneRefusal(error) ? 'calls.noMicrophone' : 'calls.failed';
    // The other side is told, or their screen rings on for a call that has
    // already gone wrong on this one.
    if (connection && callId.value) {
      connection.send({
        type: 'call.end',
        conversationId: conversationId.value ?? '',
        payload: { callId: callId.value, reason: 'failed' },
      });
    }
    endReason.value = 'failed';
    phase.value = 'failed';
    teardown();
  }

  /**
   * A refusal sent without a call of our own — "busy", or a microphone denied
   * while answering.
   *
   * It needs a socket, and in the busy case there is already one open for a
   * different conversation. So this opens a throwaway one, sends, and closes.
   */
  function declineFromElsewhere(
    conversation: string,
    id: string,
    reason: CallEndReason,
  ): void {
    if (!id) return;
    const temporary = new RealtimeConnection(conversation, {
      onFrame: () => undefined,
      onStateChange: (state) => {
        if (state !== 'connected') return;
        temporary.send({
          type: 'call.end',
          conversationId: conversation,
          payload: { callId: id, reason },
        });
        // Closed on the next tick so the frame is flushed first.
        window.setTimeout(() => temporary.disconnect(), 250);
      },
    });
    void temporary.connect();
  }

  /** Releases everything the call held, leaving the overlay's text in place. */
  function teardown(): void {
    clearTimer('ring');
    clearTimer('reoffer');
    clearTimer('connect');
    clearTimer('tick');

    for (const track of localStream?.getTracks() ?? []) track.stop();
    localStream = null;

    // The microphone light staying on after a call is the single most alarming
    // thing a call feature can do, so the tracks are stopped above before
    // anything that might throw.
    peerConnection?.close();
    peerConnection = null;
    pendingCandidates = [];
    localCandidates = [];
    localOffer = null;

    if (remoteAudio) {
      remoteAudio.pause();
      remoteAudio.srcObject = null;
      remoteAudio = null;
    }

    connection?.disconnect();
    connection = null;
  }

  /** Back to nothing at all, text included. */
  function reset(): void {
    teardown();
    phase.value = 'idle';
    peer.value = null;
    conversationId.value = null;
    callId.value = null;
    muted.value = false;
    seconds.value = 0;
    endReason.value = null;
    relayMissing.value = false;
    errorKey.value = null;
  }

  function startTimer(): void {
    seconds.value = 0;
    tickTimer = window.setInterval(() => {
      seconds.value += 1;
    }, 1000);
  }

  function clearTimer(which: 'ring' | 'reoffer' | 'connect' | 'tick'): void {
    if (which === 'ring' && ringTimer !== null) {
      window.clearTimeout(ringTimer);
      ringTimer = null;
    }
    if (which === 'reoffer' && reofferTimer !== null) {
      window.clearInterval(reofferTimer);
      reofferTimer = null;
    }
    if (which === 'connect' && connectTimer !== null) {
      window.clearTimeout(connectTimer);
      connectTimer = null;
    }
    if (which === 'tick' && tickTimer !== null) {
      window.clearInterval(tickTimer);
      tickTimer = null;
    }
  }

  /** A ring that arrives for a call already over, from the user channel. */
  function remoteEnded(payload: { callId: string; reason: CallEndReason }): void {
    if (payload.callId !== callId.value) return;
    finish(payload.reason, false);
  }

  return {
    phase,
    peer,
    conversationId,
    callId,
    muted,
    seconds,
    endReason,
    relayMissing,
    errorKey,
    busy,
    inCall,
    place,
    incoming,
    accept,
    decline,
    hangUp,
    toggleMute,
    dismiss,
    remoteEnded,
  };
});

/* -------------------------------------------------------------------------- */

/**
 * A fresh id per attempt.
 *
 * `crypto.randomUUID` where it exists, which is everywhere this app runs on
 * HTTPS, and a timestamp-and-random fallback for a plain-HTTP dev server where
 * it is undefined. It only has to be unique between two people for a minute.
 */
function mintCallId(): string {
  if (typeof crypto !== 'undefined' && 'randomUUID' in crypto) return crypto.randomUUID();
  return `c${Date.now().toString(36)}${Math.random().toString(36).slice(2, 10)}`;
}

/** Whether the browser refused the microphone, as opposed to anything else. */
function microphoneRefusal(error: unknown): boolean {
  if (!(error instanceof Error)) return false;
  return (
    error.name === 'NotAllowedError' ||
    error.name === 'NotFoundError' ||
    error.name === 'NotReadableError'
  );
}

/** The other person in a 1:1, in the shape a ring carries. */
export function callPeerOf(user: Pick<PublicUser, 'id' | 'username' | 'displayName' | 'avatarUrl'>): CallPeer {
  return {
    id: user.id,
    username: user.username,
    displayName: user.displayName,
    avatarUrl: user.avatarUrl,
  };
}
